import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowRight } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { useSmoothScroll } from "@/context/SmoothScrollContext";
import { content, heroSlides } from "@/i18n/content";
import { EASE } from "@/components/Reveal";
import { avifSrc, isLocalPhoto, webpSrc } from "@/lib/media";

const SLIDE_MS = 6500;
// Alternate Ken Burns drift direction per slide so the pan never repeats identically back to back.
const KEN_BURNS = [
  { from: { scale: 1.14, x: "0%", y: "0%" }, to: { scale: 1, x: "-1.5%", y: "-1.5%" } },
  { from: { scale: 1.1, x: "-2%", y: "0%" }, to: { scale: 1.22, x: "1.5%", y: "1%" } },
  { from: { scale: 1.16, x: "1.5%", y: "-1%" }, to: { scale: 1, x: "0%", y: "1.5%" } },
];

const MaskedLine = ({ children, delay, className = "" }) => (
  <span className={`block overflow-hidden ${className}`}>
    <motion.span
      className="block will-change-transform"
      initial={{ y: "112%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.15, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

const Hero = () => {
  const { pick } = useLanguage();
  const { scrollTo } = useSmoothScroll();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const fade = useTransform(scrollYProgress, [0, 0.85], [1, 0]);

  const [slide, setSlide] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setSlide((s) => (s + 1) % heroSlides.length), SLIDE_MS);
    return () => clearInterval(id);
  }, []);

  return (
    <section ref={ref} data-testid="hero-section" className="relative h-[100svh] min-h-[620px] overflow-hidden bg-[#151515]">
      <motion.div style={{ y: bgY }} className="absolute inset-0 will-change-transform">
        <AnimatePresence initial={false}>
          {heroSlides.map((item, i) =>
            i === slide ? (
              <motion.div
                key={item.src}
                data-testid="hero-carousel-slide"
                className="absolute inset-0"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 1.4, ease: EASE }}
              >
                <picture>
                  {isLocalPhoto(item.src) && (
                    <>
                      <source srcSet={avifSrc(item.src)} type="image/avif" />
                      <source srcSet={webpSrc(item.src)} type="image/webp" />
                    </>
                  )}
                  <motion.img
                    src={item.src}
                    alt={pick(item.caption)}
                    className="h-[115%] w-full object-cover object-[center_35%]"
                    initial={KEN_BURNS[i % KEN_BURNS.length].from}
                    animate={KEN_BURNS[i % KEN_BURNS.length].to}
                    transition={{ duration: SLIDE_MS / 1000 + 1.4, ease: "linear" }}
                    fetchPriority={i === 0 ? "high" : undefined}
                    loading={i === 0 ? "eager" : "lazy"}
                  />
                </picture>
              </motion.div>
            ) : null
          )}
        </AnimatePresence>
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/30 to-black/10" aria-hidden="true" />
      <div className="absolute inset-0 bg-gradient-to-r from-black/45 via-transparent to-transparent" aria-hidden="true" />

      <div
        data-testid="hero-carousel-dots"
        className="absolute bottom-8 left-6 md:left-12 lg:left-24 z-10 flex items-center gap-2"
      >
        {heroSlides.map((item, i) => (
          <button
            key={item.src}
            type="button"
            data-testid={`hero-carousel-dot-${i}`}
            aria-label={`${pick({ en: "Slide", hi: "स्लाइड" })} ${i + 1}`}
            aria-current={i === slide}
            onClick={() => setSlide(i)}
            className={`h-1.5 rounded-full transition-all duration-500 ${
              i === slide ? "w-7 bg-[#F7F5F0]" : "w-1.5 bg-[#F7F5F0]/40 hover:bg-[#F7F5F0]/70"
            }`}
          />
        ))}
      </div>

      <motion.div
        style={{ opacity: fade }}
        className="absolute inset-x-0 bottom-0 pb-[10vh] px-6 md:px-12 lg:px-24 max-w-[1600px] mx-auto"
      >
        <MaskedLine delay={0.35}>
          <span className="inline-flex items-center gap-3 text-[11px] md:text-xs font-semibold uppercase tracking-[0.28em] text-[#F7F5F0]/85">
            <span className="inline-block h-[2px] w-10 bg-[#C42021]" aria-hidden="true" />
            {pick(content.hero.eyebrow)}
          </span>
        </MaskedLine>

        <h1 className="mt-5 font-display font-bold leading-[0.95] tracking-[-0.02em] text-[#F7F5F0] h-hero">
          {pick(content.hero.nameLines).map((line, i) => (
            <MaskedLine key={line} delay={0.5 + i * 0.14}>
              {line}
            </MaskedLine>
          ))}
        </h1>

        <MaskedLine delay={0.85} className="mt-6 max-w-2xl">
          <span className="font-display italic text-lg md:text-2xl text-[#F7F5F0]/95">
            {pick(content.hero.tagline)}
          </span>
        </MaskedLine>

        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.05, ease: EASE }}
          className="mt-4 max-w-xl text-sm md:text-base leading-relaxed text-[#F7F5F0]/75"
        >
          {pick(content.hero.sub)}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.2, ease: EASE }}
          className="mt-8 flex flex-wrap items-center gap-4"
        >
          <button
            type="button"
            data-testid="hero-join-btn"
            onClick={() => scrollTo("#contact")}
            className="group inline-flex items-center gap-3 rounded-full bg-[#C42021] px-7 py-4 text-xs md:text-sm font-semibold uppercase tracking-[0.16em] text-[#F7F5F0] transition-colors duration-300 hover:bg-[#F7F5F0] hover:text-[#171717]"
          >
            {pick(content.hero.ctaPrimary)}
            <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
          </button>
          <button
            type="button"
            data-testid="hero-priorities-btn"
            onClick={() => scrollTo("#priorities")}
            className="inline-flex items-center rounded-full border border-[#F7F5F0]/50 px-7 py-4 text-xs md:text-sm font-semibold uppercase tracking-[0.16em] text-[#F7F5F0] transition-colors duration-300 hover:border-[#F7F5F0] hover:bg-[#F7F5F0]/10"
          >
            {pick(content.hero.ctaSecondary)}
          </button>
        </motion.div>
      </motion.div>

      <motion.button
        type="button"
        data-testid="hero-scroll-indicator"
        onClick={() => scrollTo("#about")}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8, duration: 0.8 }}
        className="absolute bottom-8 right-6 md:right-12 lg:right-24 hidden md:flex flex-col items-center gap-3 text-[#F7F5F0]/70 hover:text-[#F7F5F0] transition-colors"
        aria-label={pick(content.hero.scroll)}
      >
        <span className="text-[10px] uppercase tracking-[0.3em] [writing-mode:vertical-rl]">
          {pick(content.hero.scroll)}
        </span>
        <motion.span animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 1.8, ease: "easeInOut" }}>
          <ArrowDown size={16} />
        </motion.span>
      </motion.button>
    </section>
  );
};

export default Hero;
